import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FlaskConical, MicroscopeIcon, Users, FileSearch, CheckCircle, Droplets, Repeat, TestTube, Network, BrainCircuit, Microscope, UserCheck, BarChart4, Scale, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const ResearchPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const researchProjects = [{
    id: 1,
    title: 'Probiotic Colonization Study',
    category: 'Mechanistic Research',
    description: 'Investigating the colonization potential of 15 commercial probiotic strains using advanced microbiome analysis.',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
  }, {
    id: 2,
    title: 'Microbiome Supplement Efficacy Trial',
    category: 'Clinical & Translational Studies',
    description: 'A 60-person study evaluating the effects of a prebiotic-probiotic combination on gut barrier function and inflammation markers.',
    image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
  }, {
    id: 3,
    title: 'Heavy Metal Analysis in Supplements',
    category: 'Product Validation Studies',
    description: 'Comprehensive testing of 50 popular supplements for heavy metal contamination and label accuracy.',
    image: 'https://images.unsplash.com/photo-1579154341098-e4e158cc7f55?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
  }];
  return <div className="bg-white w-full" data-id="element-1697">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-1698">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1699">
          <div className="text-center" data-id="element-1700">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-1701">
              Research at Ukweli Institute
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-1702">
              Setting new standards in microbiome and therapeutic science with
              rigorous, independent research
            </p>
          </div>
        </div>
      </section>
      {/* Research Overview */}
      <section className="py-16 bg-white" data-id="element-1703">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1704">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="mb-12" data-id="element-1705">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-1706">
              Our Research Approach
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-1707">
              The Ukweli Institute conducts rigorous, independent research
              across multiple disciplines to provide objective evidence about
              supplements, probiotics, and other microbiome-targeted products.
              Under the leadership of{' '}
              <Link to="/profile" className="text-blue-600 hover:text-blue-800" data-id="element-1708">
                Shawn Blake
              </Link>
              , our comprehensive approach builds a foundation of trust and
              scientific validity, unlike most supplement companies that do
              minimal research beyond marketing studies.
            </p>
          </motion.div>
          {/* Featured Research Projects */}
          <div className="mt-16 mb-16" data-id="element-1709">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-1710">
              Featured Research Projects
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1711">
              {researchProjects.map((project, index) => <motion.div key={project.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-gray-50 rounded-lg overflow-hidden shadow-md" data-id="element-1712">
                  <div className="h-48 overflow-hidden" data-id="element-1713">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-1714" />
                  </div>
                  <div className="p-6" data-id="element-1715">
                    <div className="mb-2" data-id="element-1716">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1717">
                        {project.category}
                      </span>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1718">
                      {project.title}
                    </h4>
                    <p className="text-gray-600 mb-4" data-id="element-1719">{project.description}</p>
                    <Link to="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1720">
                      Learn more <ArrowRightIcon size={16} className="ml-1" data-id="element-1721" />
                    </Link>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Research Types */}
      <section className="py-16 bg-gray-50" data-id="element-1722">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1723">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-16" data-id="element-1724">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1725">
              Types of Research at Ukweli Institute
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-1726">
              Our multi-disciplinary approach ensures comprehensive evaluation
              of microbiome-targeted products
            </p>
          </motion.div>
          {/* Product Validation Studies */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-20" data-id="element-1727">
            <div className="flex items-center mb-6" data-id="element-1728">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1729">
                <FlaskConical size={28} className="text-blue-600" data-id="element-1730" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1731">
                1. Product Validation Studies
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1732">
              We verify that supplements and therapeutic products are safe,
              effective, and truthfully labeled through rigorous analytical
              testing.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1733">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1734">
                <div className="flex items-center mb-4" data-id="element-1735">
                  <CheckCircle size={20} className="text-blue-600 mr-2" data-id="element-1736" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1737">
                    Purity & Safety
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1738">
                  Analytical chemistry to verify label claims (e.g., correct
                  dosage, no contaminants, heavy metals, or adulterants).
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1739">
                <div className="flex items-center mb-4" data-id="element-1740">
                  <Droplets size={20} className="text-blue-600 mr-2" data-id="element-1741" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1742">
                    Bioavailability Testing
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1743">
                  In vitro digestion and absorption models to test whether
                  compounds survive stomach acid and reach target tissues.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1744">
                <div className="flex items-center mb-4" data-id="element-1745">
                  <Repeat size={20} className="text-blue-600 mr-2" data-id="element-1746" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1747">
                    Batch Consistency
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1748">
                  Ongoing monitoring of supplement quality across different
                  manufacturing runs.
                </p>
              </div>
            </div>
          </motion.div>
          {/* Mechanistic Research */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-20" data-id="element-1749">
            <div className="flex items-center mb-6" data-id="element-1750">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1751">
                <MicroscopeIcon size={28} className="text-blue-600" data-id="element-1752" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1753">
                2. Mechanistic Research
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1754">
              We investigate how products work at the cellular and molecular
              level, providing deeper understanding of their effects on the
              microbiome and human physiology.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1755">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1756">
                <div className="flex items-center mb-4" data-id="element-1757">
                  <TestTube size={20} className="text-blue-600 mr-2" data-id="element-1758" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1759">
                    In Vitro Models
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1760">
                  Using petri dish assays, organoids, and 3D gut models to
                  examine whether supplements produce measurable biological
                  effects.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1761">
                <div className="flex items-center mb-4" data-id="element-1762">
                  <Network size={20} className="text-blue-600 mr-2" data-id="element-1763" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1764">
                    Pathway Testing
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1765">
                  Measuring molecular markers such as cytokine signaling, PKR
                  activity, mitochondrial stress responses, and barrier
                  integrity.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1766">
                <div className="flex items-center mb-4" data-id="element-1767">
                  <BrainCircuit size={20} className="text-blue-600 mr-2" data-id="element-1768" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1769">
                    Microbial Interaction Studies
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1770">
                  Assessing whether probiotics can colonize, disrupt biofilms,
                  or modulate harmful bacteria.
                </p>
              </div>
            </div>
          </motion.div>
          {/* Clinical & Translational Studies */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-20" data-id="element-1771">
            <div className="flex items-center mb-6" data-id="element-1772">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1773">
                <Users size={28} className="text-blue-600" data-id="element-1774" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1775">
                3. Clinical & Translational Studies
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1776">
              We conduct human trials to evaluate the real-world efficacy and
              safety of promising products in diverse populations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1777">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1778">
                <div className="flex items-center mb-4" data-id="element-1779">
                  <Microscope size={20} className="text-blue-600 mr-2" data-id="element-1780" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1781">
                    Pilot Human Trials
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1782">
                  Small-scale trials (n=30–60) to test tolerability and
                  preliminary efficacy of certified products.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1783">
                <div className="flex items-center mb-4" data-id="element-1784">
                  <Scale size={20} className="text-blue-600 mr-2" data-id="element-1785" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1786">
                    Comparative Studies
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1787">
                  Benchmarking multiple supplements in the same category to see
                  which ones deliver the strongest measurable effects.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1788">
                <div className="flex items-center mb-4" data-id="element-1789">
                  <UserCheck size={20} className="text-blue-600 mr-2" data-id="element-1790" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1791">
                    Subgroup Research
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1792">
                  Exploring how supplements work in specific populations
                  (autism, IBS, long COVID, chronic fatigue).
                </p>
              </div>
            </div>
          </motion.div>
          {/* Meta-Research & Transparency */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1793">
            <div className="flex items-center mb-6" data-id="element-1794">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1795">
                <FileSearch size={28} className="text-blue-600" data-id="element-1796" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1797">
                4. Meta-Research & Transparency
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-8 pl-16" data-id="element-1798">
              We analyze existing research and conduct comparative studies to
              provide consumers and practitioners with clear, unbiased
              information.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pl-16" data-id="element-1799">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1800">
                <div className="flex items-center mb-4" data-id="element-1801">
                  <FileSearch size={20} className="text-blue-600 mr-2" data-id="element-1802" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1803">
                    Evidence Reviews
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1804">
                  Analyzing and publishing transparent reports on existing
                  literature for widely used supplements.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1805">
                <div className="flex items-center mb-4" data-id="element-1806">
                  <BarChart4 size={20} className="text-blue-600 mr-2" data-id="element-1807" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1808">
                    Consumer Reports-Style Studies
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1809">
                  Head-to-head product comparisons with open publication of
                  data.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1810">
                <div className="flex items-center mb-4" data-id="element-1811">
                  <CheckCircle size={20} className="text-blue-600 mr-2" data-id="element-1812" />
                  <h4 className="text-lg font-semibold text-gray-900" data-id="element-1813">
                    Certification Integration
                  </h4>
                </div>
                <p className="text-gray-700" data-id="element-1814">
                  Feeding results back into the Ukweli Certified™ three-tier
                  system (Evidence Screen → Mechanistic Validation → Clinical
                  Validation).
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Why This Matters */}
      <section className="py-16 bg-white" data-id="element-1815">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1816">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-50 rounded-xl p-8 md:p-12" data-id="element-1817">
            <div className="flex items-center mb-6" data-id="element-1818">
              <div className="bg-blue-600 p-2 rounded-full mr-4 text-white" data-id="element-1819">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1820">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-1821" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1822">
                Why This Matters
              </h3>
            </div>
            <p className="text-lg text-gray-700 mb-6" data-id="element-1823">
              Most supplement companies do minimal or no research beyond
              marketing studies. By carrying out its own in-house validation and
              independent trials, the Ukweli Institute:
            </p>
            <ul className="space-y-4 text-gray-700" data-id="element-1824">
              <li className="flex items-start" data-id="element-1825">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1826">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1827">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1828" />
                  </svg>
                </div>
                <p className="text-lg" data-id="element-1829">
                  <strong data-id="element-1830">Provides objective evidence</strong> that products are
                  safe and effective.
                </p>
              </li>
              <li className="flex items-start" data-id="element-1831">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1832">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1833">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1834" />
                  </svg>
                </div>
                <p className="text-lg" data-id="element-1835">
                  <strong data-id="element-1836">Creates a trustworthy benchmark</strong> for
                  consumers, clinicians, and regulators.
                </p>
              </li>
              <li className="flex items-start" data-id="element-1837">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1838">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1839">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1840" />
                  </svg>
                </div>
                <p className="text-lg" data-id="element-1841">
                  <strong data-id="element-1842">Builds a feedback loop</strong> where research →
                  certification → consumer guidance → higher industry standards.
                </p>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gray-50" data-id="element-1843">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1844">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1845">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1846">
              Partner with Us on Research
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-1847">
              Interested in collaborating on research or having your products
              evaluated by our independent team? Contact us to learn more about
              our research partnerships and certification process.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1848">
              <Link to="/contact" className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300" data-id="element-1849">
                Contact Our Research Team
              </Link>
              <Link to="/certification" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-gray-100 transition-colors duration-300" data-id="element-1850">
                Learn About Certification
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};